import type { ComputedRef, Ref } from 'vue'
import type { ActiveScratchCard } from '~/composables/useScratchCard'
import type { ScratchCardProgress, ScratchEmailNotice, ScratchFlowStep } from '~/utils/scratchStore'
import { useMarbleAnnouncement } from '~/composables/useMarbleAnnouncement'
import { footerNewsletterInput } from '~/utils/announcementAnchor'
import {
  canManualOpen,
  markCard,
  readLeadToken,
  readScratchStore,
  resolveStartStep,
  writeScratchStore,
} from '~/utils/scratchStore'

/**
 * Scratch to win: the half of the flow every route pays for.
 *
 * Three callers reach the flow from code that is loaded on every page —
 * `ScratchTrigger` (mounted for the whole visit inside app.vue) and
 * `useScratchTrigger` with it, the announcement strip's opener through
 * `useNewsletterModal`, and the Dashboard's preview page, which the build folds
 * into the entry chunk because it is too small to be worth one of its own. All
 * three only ever OPEN a card. Until this split they asked `useScratchFlow` for
 * that, and the entire state machine came along for the ride: 10.6 KB raw of
 * `useScratchFlow`, 2.0 KB of `scratchCopy`, and — through `submitPhone`'s
 * `isValidOrderPhoneNumber` — 120.7 KB of libphonenumber-js (83.6 KB of min
 * metadata plus its es6 parser core), the single largest thing in the
 * 2026-09-22 production entry chunk (source-map attribution, raw bytes).
 *
 * So the line is drawn where the two audiences actually part: OPENING a card —
 * the state, who may see it, the snapshot, and the open/close verbs — is shared
 * and lives here; PLAYING one — the steps, the submits, the wording, the
 * refusals — is `useScratchFlow`, which builds on this module and ships with the
 * already-lazy overlay that is the only thing that uses it.
 *
 * Two things must not be duplicated into the overlay half, which is why they are
 * here and only here: the `useState` keys, which ARE the page-life state both
 * halves read and write, and the two module-scope guards below, which only mean
 * anything while there is exactly one of each.
 */

export type ScratchOpenSource = 'TIMED' | 'EXIT_INTENT' | 'MANUAL' | 'PREVIEW'
export type ScratchCloseReason = 'dismiss' | 'complete' | 'interrupted'

export interface ScratchAudienceDecision {
  show: boolean
  skipEmail: boolean
  /** A logged-in customer who has already won: never shown automatically, but the strip opens SUCCESS. */
  winner: boolean
}

export interface ScratchFlowState {
  open: boolean
  /** True from the first open on, so the lazy overlay can animate out. */
  mounted: boolean
  cardId: string | null
  step: ScratchFlowStep
  source: ScratchOpenSource | null
  /** Nothing is sent, nothing is stored, no analytics. */
  preview: boolean
  /** Logged-in customer without a phone: the phone step is the whole flow. */
  skipEmail: boolean
  /** Logged-in customer who already won (opened at SUCCESS). */
  winner: boolean
  codeSent: boolean
  /** Date.now() whenever `step` became current — the `elapsedMs` the server brakes on. */
  stepShownAt: number
  submitting: boolean
  error: { message: string, field: 'email' | 'phone' | 'consent' | null } | null
  terminal: 'ALREADY_PLAYED' | 'NOT_AVAILABLE' | null
  emailNotice: ScratchEmailNotice
  /** Prefill kept across an EMAIL detour from the phone step. */
  pendingPhone: { phoneNumber: string, countryCode: string } | null
  /** A leadToken arrived during this opening. */
  tokenFromThisOpen: boolean
  resend: 'idle' | 'sending' | 'sent'
  resendError: string
}

export const SCRATCH_FLOW_STATE_KEY = 'scratch-flow'

export function initialScratchFlowState(): ScratchFlowState {
  return {
    open: false,
    mounted: false,
    cardId: null,
    step: 'SCRATCH',
    source: null,
    preview: false,
    skipEmail: false,
    winner: false,
    codeSent: false,
    stepShownAt: 0,
    submitting: false,
    error: null,
    terminal: null,
    emailNotice: null,
    pendingPhone: null,
    tokenFromThisOpen: false,
    resend: 'idle',
    resendError: '',
  }
}

/** How long `resolveAudience` waits for the deferred activeCustomer fetch. */
const AUTH_WAIT_MS = 5000

/** One manual opening at a time, across every component that offers one. */
let manualBusy = false

/** Card ids whose `scratch_view` has been reported in this page life. */
const reportedViews = new Set<string>()

/**
 * A preview of a step shows the skip link only where the live flow would: the
 * code is already out on SUCCESS, and on the phone step of a card whose phone
 * is optional. Nothing about this is sent or stored.
 */
function previewCodeSent(step: ScratchFlowStep, card: ActiveScratchCard | null): boolean {
  return step === 'SUCCESS' || (step === 'PHONE' && !!card && !card.phoneRequired)
}

/**
 * What the always-loaded callers may touch, plus the few internals
 * `useScratchFlow` builds the rest of the machine on. Written out rather than
 * inferred for the same reason `ScratchFlow` is: the seam between the two
 * modules is the whole point of the split, so it is a declaration.
 *
 * `state` is handed out writable, not `Readonly`, because the overlay half drives
 * every field of it; the callers that only read re-declare nothing and simply do
 * not write. `scratchCard` is passed through rather than re-resolved so the
 * overlay does not start a second `useNow` ticker for the same card cache.
 */
export interface ScratchFlowCore {
  state: Ref<ScratchFlowState>
  /** The card snapshotted when the overlay opened. */
  card: ComputedRef<ActiveScratchCard | null>
  /** Writable behind `card`: the overlay half re-snapshots it when consent text changes. */
  cardSnapshot: Ref<ActiveScratchCard | null>
  /** The typed address, or the logged-in customer's. Memory only. */
  email: Ref<string>
  scratchCard: {
    card: ComputedRef<ActiveScratchCard | null>
    ensure: (options?: { force?: boolean }) => Promise<ActiveScratchCard | null>
    invalidate: () => void
  }
  resolveAudience: (options?: { kick?: boolean, manual?: boolean }) => Promise<ScratchAudienceDecision>
  openCard: (card: ActiveScratchCard, options: { source: Exclude<ScratchOpenSource, 'PREVIEW'>, audience: ScratchAudienceDecision }) => void
  openPreview: (card: ActiveScratchCard, options?: { step?: ScratchFlowStep, skipEmail?: boolean }) => void
  /** Preview-only step navigation (E's ScratchPreviewBar chips); a no-op otherwise. */
  previewStep: (step: ScratchFlowStep) => void
  requestManualOpen: () => Promise<void>
  close: (reason: ScratchCloseReason) => void
  clearError: () => void
  /** Moves to a step and restarts the `elapsedMs` clock the server brakes on. */
  goTo: (step: ScratchFlowStep) => void
  /** Records progress for the open card; a no-op in preview. */
  mark: (fields: (keyof ScratchCardProgress)[]) => void
  /** Remembers a verdict for the logged-in customer, for this page life. */
  cacheAudience: (decision: ScratchAudienceDecision) => void
  /** The logged-in customer's address, or ''. */
  accountEmail: () => string
}

export function useScratchFlowCore(): ScratchFlowCore {
  const auth = useAuth()
  const analytics = useAnalytics()
  const lead = analytics.lead
  const scratchCard = useScratchCard()
  const { fetchAudience } = useScratchSubmit()
  // Taken here, in setup: `requestManualOpen` runs from a click handler, where
  // `useState` has no Nuxt instance to reach.
  const marble = useMarbleAnnouncement()
  const router = useRouter()

  const state = useState<ScratchFlowState>(SCRATCH_FLOW_STATE_KEY, initialScratchFlowState)
  // The card the overlay is rendering, snapshotted at open: a card that ends
  // mid-flow must not blank the screen the visitor is on.
  const cardSnapshot = useState<ActiveScratchCard | null>('scratch-flow-card', () => null)
  // Memory only. The typed address (or the account's) is needed for the
  // "Sent to …" line and the `{email}` placeholder, and is never written to
  // web storage.
  const email = useState<string>('scratch-flow-email', () => '')
  const audienceCache = useState<Record<string, ScratchAudienceDecision>>('scratch-audience', () => ({}))

  const card = computed<ActiveScratchCard | null>(() => cardSnapshot.value)

  function accountEmail(): string {
    return auth?.activeCustomer.value?.emailAddress ?? ''
  }

  function mark(fields: (keyof ScratchCardProgress)[]): void {
    const id = state.value.cardId
    if (state.value.preview || !id)
      return
    writeScratchStore(markCard(readScratchStore(), id, fields, Date.now()))
  }

  function goTo(step: ScratchFlowStep): void {
    state.value.step = step
    state.value.stepShownAt = Date.now()
    state.value.error = null
  }

  function clearError(): void {
    state.value.error = null
  }

  function cacheAudience(decision: ScratchAudienceDecision): void {
    const id = auth?.activeCustomer.value?.id
    if (id != null)
      audienceCache.value = { ...audienceCache.value, [String(id)]: decision }
  }

  /** Resolves once the deferred activeCustomer fetch has settled, or times out. */
  function waitForAuth(): Promise<boolean> {
    if (auth?.isAuthResolved.value)
      return Promise.resolve(true)
    return new Promise<boolean>((resolve) => {
      let timer: ReturnType<typeof setTimeout> | undefined
      const stop = watch(() => auth?.isAuthResolved.value, (resolved) => {
        if (!resolved)
          return
        finish(true)
      })
      function finish(value: boolean) {
        stop()
        if (timer)
          clearTimeout(timer)
        resolve(value)
      }
      timer = setTimeout(() => finish(false), AUTH_WAIT_MS)
    })
  }

  /**
   * May this visitor see the card, and how much of it?
   *
   * An anonymous visitor is always allowed and costs no request: asking would
   * mint a Vendure session for someone who may never play. A logged-in customer
   * is judged by the backend (wholesale, already played, already has a phone),
   * cached per customer id for the page life. `manual` relaxes the one rule that
   * is about not interrupting rather than about eligibility — a customer with a
   * phone on the account is never shown the card unprompted, but may ask for it.
   */
  async function resolveAudience(options: { kick?: boolean, manual?: boolean } = {}): Promise<ScratchAudienceDecision> {
    const deny: ScratchAudienceDecision = { show: false, skipEmail: false, winner: false }
    if (!auth)
      return deny

    if (!auth.isAuthResolved.value) {
      if (options.kick && !auth.isAuthLoading.value)
        void auth.refreshActiveCustomer().catch(error => console.error('Failed to check the signed-in customer', error))
      if (!await waitForAuth()) {
        // Nothing is known yet. A manual ask still opens — the server stays the
        // authority and will refuse if it must.
        return { show: !!options.manual, skipEmail: false, winner: false }
      }
    }

    const customer = auth.activeCustomer.value
    if (!customer)
      return { show: true, skipEmail: false, winner: false }

    if (customer.phoneNumber?.trim() && !options.manual)
      return deny

    const cached = audienceCache.value[String(customer.id)]
    if (cached)
      return cached

    const answer = await fetchAudience()
    if (!answer)
      return deny

    const decision: ScratchAudienceDecision = {
      show: answer.show,
      skipEmail: answer.show && answer.skipEmail,
      winner: !answer.show && answer.reason === 'ALREADY_PLAYED',
    }
    audienceCache.value = { ...audienceCache.value, [String(customer.id)]: decision }
    return decision
  }

  function openCard(
    active: ActiveScratchCard,
    options: { source: Exclude<ScratchOpenSource, 'PREVIEW'>, audience: ScratchAudienceDecision },
  ): void {
    if (state.value.open)
      return
    const { audience, source } = options
    if (!audience.show && !audience.winner)
      return

    const store = readScratchStore()
    const id = String(active.id)
    const start = audience.winner
      ? { step: 'SUCCESS' as ScratchFlowStep, emailNotice: null as ScratchEmailNotice }
      : resolveStartStep(store, id, { skipEmail: audience.skipEmail, hasLeadToken: readLeadToken() !== null })

    cardSnapshot.value = active
    state.value = {
      ...initialScratchFlowState(),
      open: true,
      mounted: true,
      cardId: id,
      step: start.step,
      emailNotice: start.emailNotice,
      source,
      skipEmail: audience.skipEmail,
      winner: audience.winner,
      codeSent: audience.winner || !!store.cards[id]?.codeSentAt,
      stepShownAt: Date.now(),
    }

    if (auth?.activeCustomer.value)
      email.value = accountEmail()

    // Only an automatic opening counts as "this browser has been offered the
    // card"; asking for it from the strip does not spend the visitor's silence.
    if (source !== 'MANUAL')
      mark(['promptedAt'])

    if (!reportedViews.has(id)) {
      reportedViews.add(id)
      lead.scratchView({
        cardId: id,
        trigger: source,
        startStep: start.step,
        audience: audience.skipEmail || audience.winner ? 'account' : 'anonymous',
      })
    }
  }

  /** The Dashboard's "Preview on shop": ignores storage, audience, trigger and dedupe. */
  function openPreview(
    active: ActiveScratchCard,
    options: { step?: ScratchFlowStep, skipEmail?: boolean } = {},
  ): void {
    const step = options.step ?? 'SCRATCH'
    cardSnapshot.value = active
    state.value = {
      ...initialScratchFlowState(),
      open: true,
      mounted: true,
      preview: true,
      cardId: String(active.id),
      step,
      source: 'PREVIEW',
      skipEmail: options.skipEmail ?? false,
      codeSent: previewCodeSent(step, active),
      stepShownAt: Date.now(),
    }
    email.value = ''
  }

  /**
   * The ONLY way the overlay moves between steps in preview: E disables every
   * submit, skip and resend control while `state.preview`, so the owner walks
   * the screens with the preview bar's chips. Stores nothing, sends nothing,
   * reports nothing, and never touches `terminal`, `resend` or the lead token.
   *
   * Here rather than with the step machine so that `pages/preview/scratch-card.vue`
   * — which the build folds into the entry chunk — needs nothing from the overlay
   * half; the chips themselves are the overlay's and reach it through
   * `useScratchFlow`.
   */
  function previewStep(step: ScratchFlowStep): void {
    if (!state.value.preview)
      return
    goTo(step)
    state.value.codeSent = previewCodeSent(step, card.value)
  }

  /**
   * The footer newsletter form, for a visitor this game has nothing left to
   * offer. It promises the same 20 % the announcement strip's newsletter line
   * does, so the click is never a dead end.
   */
  function scrollToFooterNewsletter(): void {
    // The same shared field the announcement bar's `#newsletter` links target.
    const el = footerNewsletterInput(document)
    if (!el)
      return
    el.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'center',
    })
    // The footer hydrates on visibility (`layouts/default.vue`), so the focus
    // waits for the scroll to have brought it into view and booted it.
    setTimeout(() => el.focus({ preventScroll: true }), 600)
  }

  /**
   * The announcement strip's opener: ignores the 30-day silence. With no
   * active card, it takes the visitor to the footer newsletter form without
   * waiting for the deferred auth check.
   */
  async function requestManualOpen(): Promise<void> {
    if (import.meta.server || state.value.open || marble.open.value || manualBusy)
      return
    manualBusy = true
    // The answer can take a round trip. Whatever happened meanwhile — the
    // exit-intent trap opened one of the popups, the visitor moved on to
    // another page — this click no longer gets to open anything. The path, not
    // the full URL, as `useScratchTrigger` counts navigations: infinite scroll
    // rewrites `?page=` on its own.
    const clickedOn = router.currentRoute.value.path
    const overtaken = () => state.value.open || marble.open.value || router.currentRoute.value.path !== clickedOn
    try {
      const store = readScratchStore()
      const active = await scratchCard.ensure()
      if (overtaken())
        return
      if (!active) {
        scrollToFooterNewsletter()
        return
      }
      const audience = await resolveAudience({ kick: true, manual: true })
      if (overtaken())
        return
      // A winner asked for their card back: SUCCESS, with the resend link.
      if (audience.winner) {
        openCard(active, { source: 'MANUAL', audience })
        return
      }
      if (!audience.show || store.playedAt || !canManualOpen(store, String(active.id))) {
        scrollToFooterNewsletter()
        return
      }
      openCard(active, { source: 'MANUAL', audience })
    }
    finally {
      manualBusy = false
    }
  }

  /**
   * Closing. Only a deliberate dismissal buys the 30-day silence — the old
   * newsletter popup recorded one on EVERY close, including after a successful
   * sign-up, which is how one mistaken click used to cost a visitor the offer.
   * `'complete'` was already recorded when SUCCESS was reached, and
   * `'interrupted'` (the cart drawer opened, a suppressed route, the card
   * ended) records nothing at all.
   */
  function close(reason: ScratchCloseReason): void {
    if (!state.value.open)
      return

    const id = state.value.cardId
    if (reason === 'dismiss' && !state.value.preview && id) {
      const store = readScratchStore()
      const progress = store.cards[id]
      if (!progress?.completedAt && !state.value.terminal && !store.playedAt && !state.value.winner)
        writeScratchStore(markCard(store, id, ['dismissedAt'], Date.now()))
    }

    state.value.open = false
    state.value.error = null
    state.value.submitting = false
    state.value.resend = 'idle'
    state.value.resendError = ''
  }

  return {
    state,
    card,
    cardSnapshot,
    email,
    scratchCard,
    resolveAudience,
    openCard,
    openPreview,
    previewStep,
    requestManualOpen,
    close,
    clearError,
    goTo,
    mark,
    cacheAudience,
    accountEmail,
  }
}
